// RevisionModal — send a piece back to an earlier pipeline stage with notes.
//
// CORRECTED to the design-system equity (the `rev-*` system from the design
// handoff). The previous version borrowed the customer-modal shell (`cust-*`),
// used inline styles, a plain <select> for the stage, and — worst — reused the
// green/red ARTICLE DECISION buttons (`btn-approve` / `btn-reject`) as
// Cancel / Send. Those buttons mean "approve / reject the article", not
// "cancel / submit a dialog", so the modal read as off-brand and semantically
// wrong.
//
// Data contract is unchanged: onSave(target, notes). Severity is folded into
// the notes the chosen agent reads verbatim, so no backend change is required.
// Requires the `rev-*` block in revision-modal.css to be present in styles.css.

import { useEffect, useState } from "react";
import { completedPhaseIndex, reachableStageValues } from "@shared/revision-stages";

interface Stage {
  value: string; // pipeline target — matches the API target enum
  agent: string;
  verb: string;
  desc: string;
}

// Ordered closest-to-current (−1) → broadest impact (−7), same as the design
// reference. `value` matches the existing API target enum exactly.
//
// Non-fiction pipeline only. Fiction pieces use a different stage list
// (Worldbuilder/Architect/Storyteller/...) this modal doesn't support, and
// the backend's REVISION_PHASE_RESET map (apps/api/src/routes/pipeline.ts)
// has no fiction targets either — that's a separate, larger gap, not
// addressed here.
const STAGES: Stage[] = [
  { value: "art_director",  agent: "Art Director", verb: "regenerate images",      desc: "Re-run cover and inline image generation." },
  { value: "seo_optimizer", agent: "SEO",          verb: "rescore AEO / GEO",      desc: "Recompute SEO/AEO/GEO scoring without rewrites." },
  { value: "editor",        agent: "Editor",       verb: "polish & tighten",       desc: "Line edit and tone pass on existing draft." },
  { value: "auditor",       agent: "Auditor",      verb: "fact-check again",       desc: "Verify claims, sources, dates and numbers." },
  { value: "writer",        agent: "Writer",       verb: "rewrite the draft",      desc: "Full rewrite from the brief and source library." },
  { value: "librarian",     agent: "Librarian",    verb: "rebuild source library", desc: "Re-organize references; deduplicate sources." },
  { value: "scout",         agent: "Scout",        verb: "find new sources",       desc: "Start over from research — broadest impact." },
];

// FEAT-108 / S-438 — the reachability rule lives in src/lib/revision-stages.ts
// so POST /pieces/:id/decision accepts exactly the stages this modal offers.
export { completedPhaseIndex, reachableStageValues };

/**
 * The design's floor: Send arms only past **three words**, not a character
 * count. "fix it" is 6 characters and passes a length check while telling the
 * agent nothing; three words is the smallest instruction that can carry a
 * subject and a verb. Pure and exported so the rule is tested rather than
 * inferred from the disabled attribute.
 */
export const NOTES_WORD_FLOOR = 3;

export function countNoteWords(notes: string): number {
  const t = notes.trim();
  return t ? t.split(/\s+/).length : 0;
}

export function notesMeetFloor(notes: string): boolean {
  return countNoteWords(notes) >= NOTES_WORD_FLOOR;
}

const SEVERITIES: Array<{ id: string; label: string; hint: string }> = [
  { id: "tweak",  label: "Tweak",  hint: "Small adjustments" },
  { id: "rework", label: "Rework", hint: "Substantial changes" },
  { id: "redo",   label: "Redo",   hint: "Start the stage fresh" },
];

const TEMPLATES: Array<{ id: string; label: string; text: string }> = [
  { id: "tone",   label: "Tone is off",          text: "The voice drifts from our editorial style — please tighten toward the brand guide. Keep the structure as-is." },
  { id: "claim",  label: "Claim needs a source", text: "Several claims need stronger citations. Verify and link primary sources where possible." },
  { id: "intro",  label: "Weak intro",           text: "The opening doesn't earn the reader. Rewrite the lede with a sharper hook and a clearer promise." },
  { id: "images", label: "Images don't fit",     text: "Cover and inline imagery feel generic. Try a more editorial direction — see the art reference brief." },
  { id: "length", label: "Too long",             text: "Trim to target word count by cutting examples and tightening transitions; preserve the argument." },
];

interface Props {
  initialTarget?: string;
  initialNotes?: string;
  /** Piece's current generation_phases — determines which stages are reachable. */
  generationPhases?: string | null;
  onCancel: () => void;
  onSave: (target: string, notes: string) => void;
  saving?: boolean;
  /**
   * BUG-105 — why the last save was refused (e.g. the piece published while
   * this was open). Shown inside the modal, which stays open with the notes.
   */
  error?: string | null;
}

export function RevisionModal({
  initialTarget = "writer",
  initialNotes = "",
  generationPhases,
  onCancel,
  onSave,
  saving,
  error,
}: Props) {
  // Only stages the piece has actually completed are numbered/selectable —
  // renumbered relative to the piece's current position, not the full
  // 7-stage list. A piece that's only finished Writer sees Writer as "-1",
  // not "-5"; Auditor/Editor/SEO/Art Director show no number and are locked.
  const reachableValues = new Set(reachableStageValues(generationPhases));
  const reachable = STAGES.filter((s) => reachableValues.has(s.value));

  const [target, setTarget] = useState(() =>
    reachableValues.has(initialTarget) ? initialTarget : (reachable[0]?.value ?? "")
  );
  const [severity, setSeverity] = useState("rework");
  const [notes, setNotes] = useState(initialNotes);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onCancel]);

  const stage = STAGES.find((s) => s.value === target) ?? reachable[0] ?? STAGES[4];
  const stepsBack = reachable.findIndex((s) => s.value === target) + 1;

  const wordCount = countNoteWords(notes);
  const meetsFloor = notesMeetFloor(notes);
  const canSubmit = meetsFloor && !saving && reachableValues.has(target);

  const applyTemplate = (t: (typeof TEMPLATES)[number]) => {
    setNotes((prev) => (prev ? `${prev}\n\n${t.text}` : t.text));
  };

  const submit = () => {
    if (!canSubmit) return;
    const sev = SEVERITIES.find((s) => s.id === severity);
    // Fold severity into the verbatim notes the agent reads — no backend change.
    const composed = sev
      ? `Severity: ${sev.label} (${sev.hint.toLowerCase()}).\n\n${notes.trim()}`
      : notes.trim();
    onSave(target, composed);
  };

  return (
    <div
      className="rev-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="rev-modal" role="dialog" aria-labelledby="rev-title">
        <header className="rev-head">
          <div className="rev-head-l">
            <div className="rev-eyebrow">Revision · pipeline restart</div>
            <h2 id="rev-title" className="rev-title">
              Send back for revision
            </h2>
          </div>
          <button className="rev-x" onClick={onCancel} aria-label="Close">
            ×
          </button>
        </header>

        <div className="rev-body">
          <p className="rev-lede">
            The piece moves back to the chosen pipeline stage. The operator picks
            it up on the next pipeline run — typically within a few minutes.
          </p>

          {/* Stage picker — visual list, not a dropdown */}
          <div className="rev-section">
            <div className="rev-section-head">
              <span className="rev-section-label">Restart from</span>
              <span className="rev-section-aside">
                {reachable.length > 0
                  ? `${stepsBack} of ${reachable.length} stages back`
                  : "No completed stages yet"}
              </span>
            </div>
            <div className="rev-stages">
              {STAGES.map((s) => {
                const isReachable = reachableValues.has(s.value);
                const num = isReachable
                  ? reachable.findIndex((r) => r.value === s.value) + 1
                  : null;
                return (
                  <button
                    key={s.value}
                    className={`rev-stage ${target === s.value ? "is-on" : ""} ${isReachable ? "" : "is-locked"}`}
                    onClick={() => isReachable && setTarget(s.value)}
                    disabled={!isReachable}
                    title={isReachable ? undefined : `${s.agent} hasn't run yet`}
                  >
                    <span className="rev-stage-step mono">
                      {num !== null ? `−${num}` : ""}
                    </span>
                    <span className="rev-stage-body">
                      <span className="rev-stage-agent">{s.agent}</span>
                      <span className="rev-stage-verb">{s.verb}</span>
                    </span>
                    <span className="rev-stage-radio" aria-hidden="true">
                      <span className="rev-stage-radio-inner" />
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="rev-stage-desc">
              <span className="mono rev-stage-desc-tag">→</span>
              {stage.desc}
            </div>
          </div>

          {/* Severity */}
          <div className="rev-section">
            <div className="rev-section-head">
              <span className="rev-section-label">Severity</span>
            </div>
            <div className="rev-severity">
              {SEVERITIES.map((s) => (
                <button
                  key={s.id}
                  className={`rev-sev ${severity === s.id ? "is-on" : ""}`}
                  onClick={() => setSeverity(s.id)}
                >
                  <span className="rev-sev-label">{s.label}</span>
                  <span className="rev-sev-hint">{s.hint}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="rev-section">
            <div className="rev-section-head">
              <span className="rev-section-label">
                Notes for the {stage.agent}
              </span>
              <span className={`rev-section-aside ${meetsFloor ? "" : "is-warn"}`}>
                {meetsFloor
                  ? `${wordCount} word${wordCount === 1 ? "" : "s"}`
                  : "Required — be specific"}
              </span>
            </div>
            <textarea
              className="rev-textarea"
              rows={5}
              placeholder={`Tell the ${stage.agent} exactly what to change and why. They read this verbatim and act on it.`}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              autoFocus
            />
            <div className="rev-templates">
              <span className="rev-templates-label mono">Quick add:</span>
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  className="rev-template"
                  onClick={() => applyTemplate(t)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="login-error" role="alert" style={{ margin: "8px 24px" }}>
            {error}
          </div>
        )}
        <footer className="rev-foot">
          <div className="rev-foot-l">
            <span className="rev-foot-meta mono">
              {reachable.length > 0 ? (
                <>
                  Restart at <strong>{stage.agent}</strong> · ~{stepsBack * 2}–
                  {stepsBack * 4} min
                </>
              ) : (
                "Nothing has run yet — no stage to restart"
              )}
            </span>
          </div>
          <div className="rev-actions">
            <button
              className="rev-btn rev-btn-ghost"
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              className="rev-btn rev-btn-primary"
              disabled={!canSubmit}
              onClick={submit}
            >
              {saving ? "Saving…" : "Send for revision"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
